import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import { getContent, asset } from '@/lib/api';
import { Seo } from '@/components/Seo';
import { PageHero } from '@/components/PageHero';
import { SectionHeading } from '@/components/SectionHeading';
import { Icon } from '@/components/Icons';
import { RevealGroup, RevealItem } from '@/components/Reveal';
import { CtaSection } from '@/components/CtaSection';

export default function Services() {
  const [services, setServices] = useState([]);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    getContent('services').then(setServices).catch(() => {});
    getContent('capability_groups').then(setGroups).catch(() => {});
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: services.map((s, i) => ({
      '@type': 'ListItem', position: i + 1, name: s.title,
      url: `https://www.srilakshmiearthmovers.com/services/${s.slug}`,
    })),
  };

  return (
    <>
      <Seo title='Our Services' description='Excavation, filling, demolition, rock cutting, dewatering, control & chemical blasting, labour contract and machinery hiring.' path='/services' image='/media/img/excavator-action.jpg' jsonLd={jsonLd} />
      <PageHero eyebrow='Our Services' title='From ground preparation to complex earthworks.' subtitle='A full spectrum of heavy earthmoving capabilities, delivered with engineering-grade control.' image='/media/img/excavator-action.jpg' />

      <section className='bg-[#F6F5F1] py-20 lg:py-28'>
        <div className='container-x'>
          <RevealGroup className='grid gap-5 md:grid-cols-2 lg:grid-cols-3'>
            {services.map((s) => (
              <RevealItem key={s.slug}>
                <Link to={`/services/${s.slug}`} data-testid='service-card' data-cursor='EXPLORE' className='group relative block h-[340px] overflow-hidden rounded-2xl border border-[#20272A]/10'>
                  <img src={asset(s.image)} alt={s.title} className='absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105' />
                  <div className='absolute inset-0' style={{ background: 'linear-gradient(180deg, rgba(32,39,42,0.1) 0%, rgba(32,39,42,0.55) 55%, rgba(32,39,42,0.95) 100%)' }} />
                  <span className='absolute left-0 top-0 h-full w-[2px] origin-top scale-y-0 bg-[#B88A3B] transition-transform duration-500 group-hover:scale-y-100' />
                  <div className='absolute inset-x-0 bottom-0 p-6'>
                    <div className='flex items-center gap-2 text-[#B88A3B]'>
                      <Icon name={s.icon} size={20} />
                      <span className='eyebrow text-[10px] text-[#F6F5F1]/60'>{s.category}</span>
                    </div>
                    <div className='mt-3 flex items-center justify-between'>
                      <h3 className='font-head text-2xl font-semibold text-[#F6F5F1]'>{s.title}</h3>
                      <ArrowUpRight size={22} className='text-[#F6F5F1]/50 transition-all duration-300 group-hover:text-[#B88A3B]' />
                    </div>
                    <p className='mt-2 max-h-0 overflow-hidden text-sm text-[#F6F5F1]/70 opacity-0 transition-all duration-500 group-hover:max-h-24 group-hover:opacity-100'>{s.tagline}</p>
                  </div>
                </Link>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Full capabilities */}
      <section className='relative bg-[#20272A] py-20 lg:py-28'>
        <div className='grain absolute inset-0' />
        <div className='relative container-x'>
          <SectionHeading eyebrow='Full Capabilities' title='Everything we do, in one place.' light />
          <div className='mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4'>
            {groups.map((g) => (
              <div key={g.group} className='border-t border-white/10 pt-5'>
                <h3 className='font-head text-lg font-semibold text-[#B88A3B]'>{g.group}</h3>
                <ul className='mt-4 space-y-2.5'>
                  {g.items.map((it) => (
                    <li key={it} className='flex items-start gap-2 text-sm text-[#F6F5F1]/70'>
                      <Check size={15} className='mt-0.5 shrink-0 text-[#075B61]' style={{ color: '#5fb6bd' }} /> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
